import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import DropDownItem from './DropDownItem';
import handleKeyDown from '../../utils/shared/accessibilityUtils';
import Button from '../button/Button';
import { CLASS } from '../../constants/shared/CommonConstants';
import './DropDown.scss';
import { generateReactKey } from '../../utils/shared/commonUtils';

const { COMMON_BUTTON_CLASSNAME } = CLASS;
const COMMON_DROPDOWN_WRAPPER_CLASS_NAME = 'dropdown-wrapper';
const COMMON_DROPDOWN_CLASSNAME = 'mclass-dropdown';
const COMMON_DROPDOWN_ITEM_CLASS_NAME = 'mclass-dropdown-item';
const COMMON_DROPDOWN_MENU_CLASS_NAME = 'mclass-dropdown-menu';

const DropDown = ({
    label,
    isDisabled,
    chosenItem,
    isOverflowed,
    onDropDownItemClick,
    dropDownClassNames,
    buttonClassNames,
    dropDownItemClassNames,
    items,
    buttons,
    isDemoModeOn,
}) => {
    const dropDownMenuRef = useRef();
    const buttonRef = useRef();
    const [isExpanded, setExpanded] = useState(false);
    const [selectedItem, setSelectedItem] = useState(false);

    const onBlurDropDown = ({ currentTarget }) => {
        // onBlur should be triggered after onClick
        setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
                setExpanded(false);
            }
        }, 0);
    };

    const isItemSelected = itemUid => itemUid === selectedItem;

    const onDropDownClick = () => {
        setExpanded(!isExpanded);
        buttonRef.current.focus();
    };

    const onDropDownItemClickHandler = id => {
        onDropDownItemClick(id);
        if (buttons) {
            setSelectedItem(id);
        } else {
            onDropDownClick();
        }
    };

    return (
        <div
            className={`${COMMON_DROPDOWN_WRAPPER_CLASS_NAME} ${
                isOverflowed ? 'overflow-visible' : ''
            }`}
            tabIndex={-1}
            role="button"
            onBlur={e => onBlurDropDown(e)}
            onKeyDown={e => handleKeyDown(e, isExpanded, dropDownMenuRef, onDropDownClick)}
            custom-attribute={label}
        >
            {!isNil(label) && <span>{label}:</span>}
            <div
                className={`${COMMON_DROPDOWN_CLASSNAME} ${dropDownClassNames} ${
                    isExpanded ? 'show' : ''
                }`}
            >
                <Button
                    id={label}
                    className={`${COMMON_BUTTON_CLASSNAME} ${buttonClassNames}`}
                    text={chosenItem.name}
                    hasPopup
                    isDisabled={isDemoModeOn ? true : isDisabled}
                    ref={buttonRef}
                    onClick={onDropDownClick}
                />
                <ul className={COMMON_DROPDOWN_MENU_CLASS_NAME} ref={dropDownMenuRef}>
                    {items.map(
                        item =>
                            !isEqual(chosenItem.uid, item.uid) && (
                                <DropDownItem
                                    key={generateReactKey(item.uid)}
                                    id={item.uid}
                                    liTagClassName={`${COMMON_DROPDOWN_ITEM_CLASS_NAME} ${dropDownItemClassNames}${
                                        isItemSelected(item.uid) ? 'active' : ''
                                    }`}
                                    name={item.name}
                                    onDropDownItemClick={onDropDownItemClickHandler}
                                />
                            ),
                    )}
                    {buttons ? (
                        <li className="btn-group">
                            {buttons.map((button, index) => (
                                <Button
                                    key={generateReactKey(index)}
                                    className={button.btnClassName}
                                    text={
                                        <>
                                            <i className={button.iClassName} />
                                            {button.text}
                                        </>
                                    }
                                    onClick={() => {
                                        button.onClick(selectedItem);
                                        setExpanded(false);
                                    }}
                                />
                            ))}
                        </li>
                    ) : null}
                </ul>
            </div>
        </div>
    );
};

DropDown.defaultProps = {
    chosenItem: '',
    items: [],
    onDropDownItemClick: () => {},
    dropDownClassNames: '',
    buttonClassNames: '',
    dropDownItemClassNames: '',
    label: null,
    isDisabled: false,
    isOverflowed: false,
    buttons: null,
    isDemoModeOn: false,
};

DropDown.propTypes = {
    chosenItem: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    onDropDownItemClick: PropTypes.func,
    dropDownClassNames: PropTypes.string,
    buttonClassNames: PropTypes.string,
    dropDownItemClassNames: PropTypes.string,
    label: PropTypes.string,
    isDisabled: PropTypes.bool,
    isOverflowed: PropTypes.bool,
    buttons: PropTypes.arrayOf(PropTypes.object),
    isDemoModeOn: PropTypes.bool,
};

export default DropDown;
